// helpers/FormHelpers.js
//
// Form + View selector helpers
// - Pure selectors over forms + form.meta.views
// - No DataStore reads/writes
//

import { DataStore } from "@aws-amplify/datastore";
import { Forms, FormTypes } from "../models"; // adjust if your helpers folder is nested differently
import { PermissionType, ViewType } from "../Enums"; // Ensure ViewType is imported correctly
import * as LogHelpers from "./LogHelpers";
import * as UserHelpers from "./UserHelpers";
import * as ViewHelpers from "./ViewHelpers";


// ============================================================================
// SECTION 1 — PURE FORM + VIEW SELECTORS (no DataStore reads/writes)
// ============================================================================

// Returns true if a form with the given token exists in forms
export const hasFormToken = (formToken, forms) => {
  try {
    if (
      typeof formToken !== "string" ||
      !Array.isArray(forms) ||
      forms.length === 0
    ) {
      return false;
    }

    return forms.some(form => form?.token === formToken);
  } catch {
    return false;
  }
};

// Function to get the form from the form token
export const getForm = (formToken, forms) => {
  if (forms && Array.isArray(forms) && forms.length > 0 && formToken) {
    if (forms.length === 1) {
      return forms[0];
    }

    const selectedForm = forms.find((form) => form.token === formToken);

    return selectedForm || forms[0];
  } else {
    console.error("Cannot get InitialForm with bad data");
    return null;
  }
};

// Function to find form tokens by formType
export const getFormTokensByFormType = (forms, formType) => {
  // Call getFormsByViewType to get all matching forms
  const matchingForms = getFormsByFormType(forms, formType);

  // Extract and return tokens from each matching form
  const tokens = matchingForms.map((form) => form.token);

  return tokens; // Always return an array of tokens
};

// Function to find forms by FormTypes
export const getFormsByFormType = (forms, formType) => {
  if (!forms || !formType) {
    throw new Error(`Invalid forms: ${forms} or FormTypes: ${formType}`);
  }

  // Filter forms based on FormTypes (new root field)
  const matchingForms = forms.filter((form) => form?.type === formType);

  return matchingForms; // Always returns an array, empty if no match
};

export const resolveFormToken = ({ forms, currentFormToken, persistedFormToken, defaultFormType }) => {
  try {
    if (!Array.isArray(forms) || forms.length === 0) return { token: null, reason: "forms-invalid" };

    if (typeof currentFormToken === "string" && currentFormToken.trim().length > 0 && hasFormToken(currentFormToken, forms)) {
      return { token: currentFormToken, reason: "state-valid" };
    }

    if (typeof persistedFormToken === "string" && persistedFormToken.trim().length > 0 && hasFormToken(persistedFormToken, forms)) {
      return { token: persistedFormToken, reason: "persisted-valid" };
    }

    const fallback = getDefaultFormToken(forms, defaultFormType);
    return { token: fallback ?? null, reason: "default" };
  } catch (error) {
    return { token: null, reason: `error:${error?.message ?? String(error)}` };
  }
};

export const resolveViewForForm = ({ form, currentViewLabel, persistedViewLabel }) => {
  try {
    if (!form) return { view: null, viewLabel: null, reason: "form-null" };

    const defaultView = getInitialView(form) ?? null;
    const defaultLabel = defaultView?.viewLabel ?? null;
    if (!defaultLabel) return { view: null, viewLabel: null, reason: "default-missing" };

    if (typeof currentViewLabel === "string" && currentViewLabel.trim().length > 0) {
      const v = getViewByLabel(form, currentViewLabel);
      if (v?.viewLabel) return { view: v, viewLabel: v.viewLabel, reason: "state-valid" };
    }

    if (typeof persistedViewLabel === "string" && persistedViewLabel.trim().length > 0) {
      const v = getViewByLabel(form, persistedViewLabel);
      if (v?.viewLabel) return { view: v, viewLabel: v.viewLabel, reason: "persisted-valid" };
    }

    return { view: defaultView, viewLabel: defaultLabel, reason: "default" };
  } catch (error) {
    return { view: null, viewLabel: null, reason: `error:${error?.message ?? String(error)}` };
  }
};

export const resolveActiveReviewToken = ({ reviewTokens, activeReviewToken, defaultReviewToken }) => {
  try {
    const tokens = Array.isArray(reviewTokens) ? reviewTokens : [];
    const def = defaultReviewToken ?? null;

    if (!activeReviewToken) return { token: def, reason: "state-missing" };
    if (tokens.length === 0) return { token: null, reason: "no-review-forms" };
    if (!tokens.includes(activeReviewToken)) return { token: def, reason: "state-invalid" };

    return { token: activeReviewToken, reason: "state-valid" };
  } catch (error) {
    return { token: null, reason: `error:${error?.message ?? String(error)}` };
  }
};

// Function to find the view by ViewType
export const getViewByViewType = (forms, viewType) => {
  const form = forms.find((form) =>
    form.meta.views.some((view) => view.type === viewType)
  );
  const view = form?.meta.views.find((view) => view.type === viewType);

  return view;
};

export const getViewByLabel = (form, viewLabel) => {
  if (!form || !form.meta || !Array.isArray(form.meta.views)) return null;
  if (!viewLabel) return null;
  return form.meta.views.find((v) => v?.viewLabel === viewLabel) ?? null;
};

export const hasViewLabel = (form, viewLabel) => {
  try {
    if (!form || !form.meta || !Array.isArray(form.meta.views) || form.meta.views.length === 0) return false;
    if (typeof viewLabel !== "string" || viewLabel.trim().length === 0) return false;
    return form.meta.views.some(v => v?.viewLabel === viewLabel);
  } catch {
    return false;
  }
};

export const getCurrentFormId = (selectedProjectId, selectedView, forms) => {
  if (!selectedProjectId || !selectedView) return null;

  const selectedForm = forms.find((form) => {
    return (
      form.projectToken === selectedProjectId &&
      form.meta.views.some((view) => view.viewLabel === selectedView.viewLabel)
    );
  });
  return selectedForm ? selectedForm.id : null;
};

export const getCurrentForm = (selectedProjectId, selectedView, forms) => {
  if (!selectedProjectId || !selectedView) {
    console.log(
      "Can't get current form when selectedProjectId or selectedView are not vlaid"
    );
    return null;
  }

  const selectedForm = forms.find((form) => {
    return (
      form.projectToken === selectedProjectId &&
      form.meta.views.some((view) => view.viewLabel === selectedView.viewLabel)
    );
  });
  return selectedForm;
};

export const getDefaultFormToken = (forms, viewType = ViewType.SUBMISSIONS) => {
  try {
    if (!Array.isArray(forms) || forms.length === 0) throw new Error("Forms must be a non-empty array");

    const matchingForms = getFormsByFormType(forms, viewType);

    if (matchingForms.length === 0) {
      const projectToken = forms[0]?.projectToken ?? "unknown-project";
      throw new Error( `No forms found for viewType '${viewType}' in project '${projectToken}'` );
    }

    if (matchingForms.length === 1) return matchingForms[0].token;

    const defaultForm = matchingForms.find(
      (form) =>
        Array.isArray(form?.meta?.views) &&
        form.meta.views.some((view) => view.defaultView === true)
    );

    if (!defaultForm) {
      const projectToken = forms[0]?.projectToken ?? "unknown-project";
      throw new Error( `Multiple forms for viewType '${viewType}' but no default set in project '${projectToken}'` );
    }

    return defaultForm.token;
  } catch (err) {
    throw new Error(`getFormToken failed: ${err.message}`);
    //N.B. should handle error better including send email
  }
};


export const getDefaultReviewFormToken = (forms) => {
  if (!forms) {
    console.error("Invalid forms data.");
    return null;
  }

  const matchingForms = getFormsByFormType(forms, FormTypes.REVIEW);

  if (!matchingForms) {
    console.error("No review forms found in this project.");
    throw new Error("No review forms found in this project.");
  }

  if (matchingForms.length === 1) {
    return matchingForms[0].token;
  }

  const reviewForm = matchingForms.find((form) =>
    form.meta.views.some((view) => view.defaultView === true)
  );

  if (reviewForm) {
    return reviewForm.token;
  } else {
    console.error(`No matching default review view found for: ${reviewForm}`);
    throw new Error(`No matching default review view found for: ${reviewForm}`);
  }
};

export const getViewSelectData = (selectedProjectToken, forms) => {
  if (!selectedProjectToken || !forms) return [];

  //N.B. we should be using form.projectToken but until that is in the schema, we will use
  //the hijacated from.proectId which contains a token
  const filteredForms = forms.filter(
    (form) => form.projectToken === selectedProjectToken
  );

  return filteredForms.flatMap((form) =>
    form.meta.views.map((view) => ({
      viewLabel: view.viewLabel,
    }))
  );
};

// Function to get the initial view from a form
export const getInitialView = (initialForm) => {
  if (initialForm) {    
    if (initialForm.meta.views.length === 1) {
      return initialForm.meta.views[0];
    }

    const defaultView = initialForm.meta.views.find(
      (view) => view.defaultView === true
    );

    return defaultView;
  }
  return null;
};

// Function to get the initial form based on project token
export const getInitialForm = (initialProjectToken, forms) => {
  if (forms && Array.isArray(forms) && forms.length > 0) {
    if (forms.length === 1) {
      return forms[0];
    }

    const defaultForm = forms.find(
      (form) =>
        form.projectToken === initialProjectToken &&
        form.meta.views.some((view) => view.defaultView === true)
    );

    return defaultForm || forms[0];
  } else {
    console.error("Cannot get InitialForm with bad data");
  }
  return null;
};

// ============================================================================
// SECTION 2 — BUSINESS / DERIVATION HELPERS (no DataStore writes)
// ============================================================================

export const compileReports = (existingReports, user, selectedFormName) => {
  try {
    // Input validation
    try {
      if (
        !existingReports ||
        !Array.isArray(existingReports) ||
        existingReports.length === 0 ||
        !user
      ) {
        const errMsg = "Invalid or empty existingReports or user data.";
        throw new Error(errMsg); // Throw an error for invalid input
      }
    } catch (error) {
      // Re-throw the error to the outer try-catch
      throw new Error(`Input validation failed: ${error.message}`);
    }

    // Function to check if the user has permission to read the report
    const canReadReport = (report) => {
      try {
        switch (report.readPermissions) {
          case PermissionType.SUPER_ADMIN:
            if (user.adminLevel !== PermissionType.SUPER_ADMIN) {
              throw new Error(
                "User does not have SUPER_ADMIN permissions for this report."
              );
            }
            return true;

          case PermissionType.ADMIN:
            if (
              user.adminLevel !== PermissionType.ADMIN &&
              user.adminLevel !== PermissionType.SUPER_ADMIN
            ) {
              throw new Error(
                "User does not have ADMIN permissions for this report."
              );
            }
            return true;

          case PermissionType.REVIEWERS:
            if (
              user.adminLevel !== PermissionType.ADMIN &&
              user.adminLevel !== PermissionType.SUPER_ADMIN
            ) {
              throw new Error(
                "User does not have REVIEWER permissions for this report."
              );
            }
            return true;

          case PermissionType.INDIVIDUAL_USER:
            const individualUsers = Array.isArray(report.individualUsers)
              ? report.individualUsers
              : report.individualUsers.split(","); // Handle array or comma-delimited strings
            if (!individualUsers.includes(user.userId)) {
              throw new Error(
                "User does not have individual user permissions for this report."
              );
            }
            return true;

          default:
            throw new Error("No valid permissions found for report.");
        }
      } catch (error) {
        // Re-throw the error to the outer try-catch
        throw new Error(`Error in canReadReport: ${error.message}`);
      }
    };

    // Filter the existing reports based on the permissions logic
    const filteredExistingReports = existingReports.filter(canReadReport);

    // Return the filtered reports
    return [...filteredExistingReports];
  } catch (error) {
    // Outer catch block that handles all errors from inner try-catch blocks
    const errMsg = `Error in compileReports function in the form ${selectedFormName}: ${
      error.message
    }<p>Existing Reports: ${JSON.stringify(
      existingReports,
      null,
      2
    )}</p><p>existingReports.length: ${
      existingReports ? existingReports.length : "Null"
    }</p>`;
    console.error(errMsg, { existingReports, user }); // Log the error in the console
    LogHelpers.handleError(errMsg, user); // Report the error using handleError
    return []; // Return an empty array as a fallback value
  }
};

// ============================================================================
// SECTION 2B — CURRENT FORM PROCESSING (no DataStore writes)
// ============================================================================
//
// This is the "shrunken" version of the PCF useEffect logic.
// It computes derived values and returns them.
// App.js stays responsible for calling setters + localStorage.
//
// IMPORTANT:
// - uses ViewType enum (no string comparisons)
// - does not call setXxx
// - does not access localStorage
//
export const processCurrentForm = ({
  currentFormToken,
  currentProjectToken,
  currentView,
  forms,
  projects,
  currentProject,
  submissions,
  reviews,
  submissionToken,
  activeReviewToken,
  appUser,
}) => {
  if (typeof currentFormToken !== "string" || currentFormToken.trim().length === 0) {
    throw new Error("processCurrentFormContext: invalid currentFormToken");
  }
  if (typeof currentProjectToken !== "string" || currentProjectToken.trim().length === 0) {
    throw new Error("processCurrentFormContext: invalid currentProjectToken");
  }
  if (!Array.isArray(forms) || forms.length === 0) {
    throw new Error("processCurrentFormContext: invalid forms");
  }
  if (!Array.isArray(projects) || projects.length === 0) {
    throw new Error("processCurrentFormContext: invalid projects");
  }
  if (!submissionToken) {
    throw new Error("processCurrentFormContext: missing submissionToken");
  }
  if (!Array.isArray(submissions) && submissions !== null) {
    throw new Error("processCurrentFormContext: invalid submissions");
  }
  if (!Array.isArray(reviews) && reviews !== null) {
    throw new Error("processCurrentFormContext: invalid reviews");
  }
  if (!appUser) {
    throw new Error("processCurrentFormContext: missing user");
  }
  if (!UserHelpers) {
    throw new Error("processCurrentFormContext: missing UserHelpers");
  }
  if (!ViewHelpers) {
    throw new Error("processCurrentFormContext: missing ViewHelpers");
  }

  const resolvedForm = getForm(currentFormToken, forms);
  if (!resolvedForm) {
    throw new Error(`processCurrentFormContext: unable to resolve form token=${currentFormToken}`);
  }

  let existingView = currentView;
  if (!existingView) {
    existingView = currentView ? currentView : resolvedForm ? getInitialView(resolvedForm) : null;
  }
  if (!existingView) {
    throw new Error("processCurrentFormContext: unable to resolve current view");
  }

  let defaultReviewFormToken = activeReviewToken;
  if (!defaultReviewFormToken) {
    defaultReviewFormToken = getDefaultReviewFormToken(forms);
  }

  // Prefer passed currentProject, else resolve from projects
  const resolvedProject =
    currentProject ||
    projects.find((p) => p?.token === currentProjectToken) ||
    null;

  if (!resolvedProject) {
    throw new Error(`processCurrentFormContext: unable to resolve project token=${currentProjectToken}`);
  }

  const { allUserData, submitterData, reviewerData, adminData } =
    UserHelpers.processProjectUsers(
      resolvedProject,
      submissionToken,
      defaultReviewFormToken,
      currentProjectToken
    );

  const isReviewView = existingView.type === ViewType.REVIEWS;

  const list = isReviewView ? reviews : submissions;
  if (!Array.isArray(list)) {
    // keeps behavior consistent with your useEffect guards
    return {
      ok: false,
      reason: "Too early: submissions/reviews not ready",
    };
  }

  const submissionNumbers = list.length;

  const inCompletedFilteredSubmissionsLength = list.filter(
    (s) =>
      s.meta?.percentComplete === undefined ||
      s.meta?.percentComplete === null ||
      s.meta?.percentComplete < 100
  ).length;

  const submittedFilteredSubmissionsLength = list.filter(
    (s) => s.meta?.complete
  ).length;

  const updatedView = ViewHelpers.updateStatWidget(
    existingView,
    submissionNumbers,
    submittedFilteredSubmissionsLength,
    inCompletedFilteredSubmissionsLength,
    submitterData.length,
    reviewerData.length
  );

  const availableReports = compileReports(
    resolvedForm?.meta?.reports,
    appUser,
    resolvedForm?.meta?.name
  );

  return {
    ok: true,
    currentForm: resolvedForm,
    currentProject: resolvedProject,
    defaultReviewFormToken,
    updatedView,
    users: {
      allUserData,
      submitterData,
      reviewerData,
      adminData,
    },
    availableReports,
  };
};

// ============================================================================
// SECTION 3 — DATASTORE READ/WRITE HELPERS (mutating / persistence)
// ============================================================================

// Usage: await updateMessage(formId, { label, template|body, subject, bcc?, replyTo?, sender?, type?, recipients? })
// N.B. This will only work for the new style of messages stored as array in meta.messages2
export const updateMessage = async (formId, message) => {
  if (!formId) throw new Error("updateMessage: missing form id");
  if (!message || !message.label)
    throw new Error("updateMessage: missing message.label");

  const form = await DataStore.query(Forms, formId);
  if (!form) throw new Error(`updateMessage: form not found for id ${formId}`);

  const meta = { ...(form.meta || {}) };
  const currentArray = Array.isArray(meta.messages2) ? [...meta.messages2] : [];

  const idx = currentArray.findIndex((m) => m && m.label === message.label);

  if (idx !== -1) {
    const existing = currentArray[idx] || {};
    const updated = {
      ...existing,
      ...(message.template !== undefined ? { template: message.template } : {}),
      ...(message.body !== undefined ? { template: message.body } : {}),
      ...(message.subject !== undefined ? { subject: message.subject } : {}),
      ...(message.bcc !== undefined ? { bcc: message.bcc } : {}),
      ...(message.replyTo !== undefined ? { replyTo: message.replyTo } : {}),
      ...(message.sender !== undefined ? { sender: message.sender } : {}),
      label: message.label,
    };

    if (!isChanged(existing, updated))
      return { status: "skipped", id: form.id, target: "meta.messages2" };

    currentArray[idx] = updated;
  } else {
    const toInsert = {
      type: message.type,
      ...(message.template !== undefined ? { template: message.template } : {}),
      ...(message.body !== undefined ? { template: message.body } : {}),
      subject: message.subject,
      recipients: message.recipients || {},
      label: message.label,
      replyTo: message.replyTo || "",
      ...(message.sender ? { sender: message.sender } : {}),
      ...(message.bcc ? { bcc: message.bcc } : {}),
    };
    currentArray.push(toInsert);
  }

  await DataStore.save(
    Forms.copyOf(form, (draft) => {
      const nextMeta = { ...(draft.meta || {}) };
      nextMeta.messages2 = currentArray; // array replacement; no stringification
      draft.meta = nextMeta;
    })
  );

  return {
    status: idx === -1 ? "created" : "updated",
    id: form.id,
    target: "meta.messages2",
  };
};

// ============================================================================
// SECTION 4 — PRIVATE HELPERS (module-local)
// ============================================================================

// trim strings for comparison
const norm = (v) => (typeof v === "string" ? v.trim() : v);

// compare only the typical email-ish fields (keep it simple and robust)
const isChanged = (a = {}, b = {}) =>
  norm(a.template ?? a.body) !== norm(b.template ?? b.body) ||
  norm(a.subject) !== norm(b.subject) ||
  norm(a.replyTo) !== norm(b.replyTo) ||
  norm(a.sender) !== norm(b.sender) ||
  norm(a.bcc) !== norm(b.bcc);
